import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import AchPaymentModal from './AchPaymentModal';
import { useAuth } from './AuthContext';
import Spinner from './Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const BalanceCard = ({ theme, lowBalance, zeroBalance }) => {
  const [showModal, setShowModal] = useState(false);
  const { currentUser, refreshUserBalance } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const formattedBalance = currentUser.balance.toFixed(2);
  const navigate = useNavigate();

  const handleRefreshClick = async () => {
    setIsLoading(true);
    await refreshUserBalance();
    setIsLoading(false);
  };

  const handleButtonClick = () => {
    if (currentUser && !currentUser.SetupComplete) {
      navigate('/paymentinfo');
    } else {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      {currentUser.hasSubaccounts && currentUser.isPolicyAdmin && !currentUser.cardGroupID && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-xl p-5 mb-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon icon={faCircleInfo} className="text-amber-600 text-sm" />
            </div>
            <div>
              <h4 className="font-bold text-amber-800 mb-2">PRO TIP: Funding Your Account</h4>
              <p className="text-amber-700 text-sm leading-relaxed">
                Accounts that utilize subaccounts must be funded individually. Select the account from the dropdown above to fund the account.
              </p>
            </div>
          </div>
        </div>
      )}
      <div
        className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 p-6 mb-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
        style={{
          background: `linear-gradient(135deg, ${theme.primary}08 0%, white 50%, ${theme.primary}05 100%)`
        }}
      >
        {/* Mobile Layout */}
        <div className="block sm:hidden">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: theme.primary }}
              ></div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                {currentUser.hasSubaccounts && !currentUser.cardGroupID ? 'Policy Limit' : currentUser.prePay ? 'Available Balance' : 'Remaining Credit'}
              </span>
            </div>
            <button 
              onClick={handleRefreshClick} 
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors" 
              title="Refresh Balance"
            >
              <FontAwesomeIcon icon={faSync} className="text-xs" />
            </button>
          </div>
          
          <div className="text-center mb-3">
            <div className="text-3xl font-bold text-gray-900 mb-1">
              ${formattedBalance}
            </div>
            {(lowBalance || zeroBalance) && (
              <div className="flex items-center justify-center gap-2 text-xs">
                <div className={`w-1.5 h-1.5 rounded-full ${zeroBalance ? 'bg-red-500' : 'bg-yellow-500'} animate-pulse`}></div>
                <span className={`font-medium ${zeroBalance ? 'text-red-600' : 'text-yellow-600'}`}>
                  {zeroBalance ? 'No funds available' : 'Low balance alert'}
                </span>
              </div>
            )}
          </div>
          
          {(currentUser?.cardGroupID || (!currentUser?.cardGroupID && !currentUser.hasSubaccounts)) && (
            <div className="w-full">
              {isLoading ? (
                <div className="flex items-center justify-center h-10">
                  <Spinner />
                </div>
              ) : (
                <button
                  className={`w-full px-4 py-2.5 rounded-lg font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 text-sm ${lowBalance || zeroBalance ? 'animate-pulse' : ''}`}
                  style={{
                    backgroundColor: theme.primary,
                    boxShadow: `0 4px 20px ${theme.primary}30`
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme.secondary || theme.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme.primary;
                  }}
                  onClick={handleButtonClick}
                >
                  {currentUser && currentUser.prePay ? 'Fund Account' : 'Make A Payment'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex sm:items-center sm:justify-between w-full">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: theme.primary }}
              ></div>
              <span className="text-sm font-medium text-gray-600 uppercase tracking-wide truncate">
                {currentUser.hasSubaccounts && !currentUser.cardGroupID ? 'Policy Limit' : currentUser.prePay ? 'Available Balance' : 'Remaining Credit'}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl lg:text-5xl font-bold text-gray-900">
                ${formattedBalance}
              </span>
              <button 
                onClick={handleRefreshClick} 
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0" 
                title="Refresh Balance"
              >
                <FontAwesomeIcon icon={faSync} className="text-sm" />
              </button>
            </div>
            {(lowBalance || zeroBalance) && (
              <div className="mt-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className={`w-2 h-2 rounded-full ${zeroBalance ? 'bg-red-500' : 'bg-yellow-500'} animate-pulse`}></div>
                  <span className={`font-medium ${zeroBalance ? 'text-red-600' : 'text-yellow-600'} truncate`}>
                    {zeroBalance ? 'No funds available' : 'Low balance alert'}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {(currentUser?.cardGroupID || (!currentUser?.cardGroupID && !currentUser.hasSubaccounts)) && (
            <div className="flex items-center ml-8">
              {isLoading ? (
                <div className="flex items-center justify-center w-32 h-12">
                  <Spinner />
                </div>
              ) : (
                <button
                  className={`px-8 py-3 rounded-full font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 ${lowBalance || zeroBalance ? 'animate-pulse' : ''}`}
                  style={{
                    backgroundColor: theme.primary,
                    boxShadow: `0 4px 20px ${theme.primary}30`
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme.secondary || theme.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme.primary;
                  }}
                  onClick={handleButtonClick}
                >
                  {currentUser && currentUser.prePay ? 'Fund Account' : 'Make A Payment'}
                </button>
              )}
            </div>
          )}
        </div>
        {showModal && createPortal(
          <AchPaymentModal isOpen={showModal} onClose={handleCloseModal} />,
          document.body
        )}
      </div>
    </>
  );
};

export default BalanceCard;
